import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
} from '@mui/material';
import { ChevronRight, VisibilityOff } from '@mui/icons-material';
import React, { ReactNode, useRef, useState } from 'react';

interface IHiddenItem {
  key: string;
  label: string;
  icon: ReactNode;
  path: string;
}

interface IProps {
  items: IHiddenItem[];
  onNavigate: (path: string) => void;
}

/**
 * Menüpunkt „Ausgeblendete Elemente" — öffnet beim Überfahren (Hover) ein
 * Flyout-Untermenü mit den ausgeblendeten Einträgen. Erscheint nur, wenn es
 * überhaupt ausgeblendete (und für den Nutzer erlaubte) Einträge gibt.
 */
export const HiddenMenuFlyout = ({ items, onNavigate }: IProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const open = Boolean(anchorEl);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setAnchorEl(null), 150);
  };

  const openNow = (event: React.MouseEvent<HTMLElement>) => {
    cancelClose();
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <ListItemButton
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
        onClick={openNow}
      >
        <ListItemIcon>
          <VisibilityOff />
        </ListItemIcon>
        <ListItemText>Ausgeblendete Elemente</ListItemText>
        <ChevronRight fontSize='small' />
      </ListItemButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            onMouseEnter: cancelClose,
            onMouseLeave: scheduleClose,
            sx: { pointerEvents: 'auto' },
          },
        }}
        disableRestoreFocus
      >
        <List dense sx={{ minWidth: 220 }}>
          {items.map(item => (
            <ListItemButton
              key={item.key}
              onClick={() => {
                setAnchorEl(null);
                onNavigate(item.path);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </ListItemButton>
          ))}
        </List>
      </Popover>
    </>
  );
};
